import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  useTheme,
  Snackbar,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://wims.revisetoexcel.com';

// ── Backend attendance status enum (confirmed from API docs) ────────────────
// Values: 'present' | 'absent' | 'half_day' | 'on_leave'
const STATUS_OPTIONS = [
  { value: 'present', label: 'Present', shortLabel: 'P', color: 'success' },
  { value: 'absent', label: 'Absent', shortLabel: 'A', color: 'error' },
  { value: 'half_day', label: 'Half Day', shortLabel: 'H', color: 'warning' },
  { value: 'on_leave', label: 'On Leave', shortLabel: 'L', color: 'info' },
];

const getStatusMeta = (status) => {
  const match = STATUS_OPTIONS.find((s) => s.value === status);
  if (match) {
    return {
      label: match.label,
      shortLabel: match.shortLabel,
      color: match.color,
      icon:
        status === 'present' ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
        ) : status === 'absent' ? (
          <HighlightOffIcon sx={{ fontSize: 14 }} />
        ) : undefined,
    };
  }
  return { label: status || 'Unknown', shortLabel: '-', color: 'default', icon: undefined };
};

// ── EditAttendance ────────────────────────────────────────────────────────
// Route: /edit-student-attendance/:id  (":id" is the attendance record id,
// NOT the admission/student id)
//
// Flow:
//  1. GET  /api/admission/attendance/{id}/         → pre-fill the form
//  2. PUT  /api/admission/attendance/{id}/         → save the edits
//  3. On success → snackbar, then navigate back to the Attendance list.
const EditAttendance = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams(); // attendance record id

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Original record as returned by the API (read-only context fields live here)
  const [record, setRecord] = useState(null);

  // Editable fields
  const [status, setStatus] = useState('');
  const [timeIn, setTimeIn] = useState('');
  const [timeOut, setTimeOut] = useState('');
  const [remark, setRemark] = useState('');

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const showNotification = useCallback((message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  }, []);

  const getAuthToken = () => {
    return (
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('access_token') ||
      sessionStorage.getItem('accessToken') ||
      sessionStorage.getItem('token')
    );
  };

  // ── Load the attendance record to edit ────────────────────────────────────
  // GET /api/admission/attendance/{id}/
  // Confirmed response shape:
  // { id, admission, candidate_name, admission_code, organization: {...},
  //   branch: {...}, date, status ('present'|'absent'|'half_day'|'on_leave'),
  //   time_in, time_out, remark, marked_by, is_active }
  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      try {
        const accessToken = getAuthToken();
        const response = await axios.get(`${API_BASE_URL}/api/admission/attendance/${id}/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        const data = response.data;
        setRecord(data);
        setStatus(data.status || '');
        setTimeIn(data.time_in || '');
        setTimeOut(data.time_out || '');
        setRemark(data.remark || '');
      } catch (error) {
        console.error('Failed to fetch attendance record:', error);
        showNotification('Failed to fetch attendance record', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchAttendance();
  }, [id, showNotification]);

  // ── Save the edited record ────────────────────────────────────────────────
  // PUT /api/admission/attendance/{id}/
  // Confirmed payload shape:
  // { admission, date, status: 'present'|'absent'|'half_day'|'on_leave',
  //   time_in, time_out, remark }
  const handleSave = async () => {
    if (!status) {
      showNotification('Please select an attendance status', 'error');
      return;
    }
    setSaving(true);
    try {
      const accessToken = getAuthToken();
      const payload = {
        admission: record.admission,
        date: record.date,
        status,
      };
      if (timeIn) payload.time_in = timeIn;
      if (timeOut) payload.time_out = timeOut;
      if (remark) payload.remark = remark;

      await axios.put(`${API_BASE_URL}/api/admission/attendance/${id}/`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      showNotification('Attendance updated successfully!');
      // Give the user a moment to see the confirmation, then head back to the list.
      setTimeout(() => {
        navigate('/student-attendance');
      }, 1200);
    } catch (error) {
      console.error('Failed to update attendance record:', error);
      showNotification('Failed to update attendance record', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToAttendance = () => {
    navigate('/student-attendance');
  };

  return (
    <Container sx={{ maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pt: 1 }}>
        <IconButton onClick={handleBackToAttendance} sx={{ mr: 0.5 }} title="Back to Attendance">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          Edit Attendance
        </Typography>
      </Box>

      <Paper
        elevation={2}
        sx={{
          maxWidth: 720,
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : record ? (
          <Box sx={{ p: 3 }}>
            {/* ── Read-only student / record context ── */}
            <Grid container spacing={2.5} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.primary">
                  Candidate Name
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {record.candidate_name || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.primary">
                  Admission Code
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {record.admission_code || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.primary">
                  Date
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {record.date
                    ? new Date(record.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : '—'}
                </Typography>
              </Grid>
              {record.branch != null && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.primary">
                    Branch
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {record.branch?.name || record.branch}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            {/* ── Editable: Status ── */}
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Status
            </Typography>
            <ToggleButtonGroup
              value={status}
              exclusive
              size="small"
              onChange={(e, value) => {
                if (value !== null) setStatus(value);
              }}
              sx={{ mb: 3 }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <ToggleButton
                  key={opt.value}
                  value={opt.value}
                  sx={{
                    minWidth: 96,
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    px: 1.5,
                    color: theme.palette[opt.color].main,
                    borderColor: theme.palette.divider,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette[opt.color].main,
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: theme.palette[opt.color].dark,
                      },
                    },
                  }}
                >
                  <Tooltip title={opt.label}>
                    <span>{opt.label}</span>
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {status && (
              <Box sx={{ mb: 3 }}>
                <Chip
                  icon={getStatusMeta(status).icon}
                  label={`Currently: ${getStatusMeta(status).label}`}
                  size="small"
                  color={getStatusMeta(status).color}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            )}

            {/* ── Editable: Time In / Time Out / Remark ── */}
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Time In"
                  type="time"
                  size="small"
                  value={timeIn}
                  onChange={(e) => setTimeIn(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Time Out"
                  type="time"
                  size="small"
                  value={timeOut}
                  onChange={(e) => setTimeOut(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remark"
                  placeholder="Remark"
                  size="small"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </Grid>
            </Grid>

            {/* ── Save / Cancel ── */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleBackToAttendance}
                disabled={saving}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
               data-app-cancel="true">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  borderRadius: '8px',
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <Typography color="text.primary">Attendance record not found.</Typography>
          </Box>
        )}
      </Paper>

      {/* ── Snackbar ── */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditAttendance;